import { useEffect, useRef } from 'react';
import { useHandStore, type HandId } from '@store/index';
import { useRafLoop } from '@hooks/index';
import { UI } from '@config/constants';

/** Ring (13–16) and pinky (17–20) landmarks are dimmed: the classifier
 *  consults ring only for 3-finger alignment and pinky is unused for any
 *  sound gesture — fading keeps the eye on the active thumb/index/middle. */
const DIM_LANDMARKS = new Set([13, 14, 15, 16, 17, 18, 19, 20]);

const ACTIVE_CONNECTIONS: readonly [number, number][] = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8],
  [5, 9], [9, 10], [10, 11], [11, 12],
];
const DIM_CONNECTIONS: readonly [number, number][] = [
  [9, 13], [13, 14], [14, 15], [15, 16],
  [13, 17], [17, 18], [18, 19], [19, 20],
  [0, 17],
];
const DIM_ALPHA = 0.25;

const COLORS: Record<HandId, string> = {
  left: UI.COLORS.LEFT_HAND,
  right: UI.COLORS.RIGHT_HAND,
};

/**
 * Canvas overlay that draws landmarks for both hands.
 * Subscribes to handStore via refs to avoid re-rendering; reads in rAF.
 */
export function HandOverlay() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * devicePixelRatio;
      canvas.height = rect.height * devicePixelRatio;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  useRafLoop(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { left, right } = useHandStore.getState();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';

    for (const snap of [left, right]) {
      if (!snap.present || !snap.landmarks) continue;
      // Color by physical hand derived from landmark x-position, not
      // from MediaPipe's handedness label — the label occasionally
      // flips frame-to-frame on the same physical hand, which would
      // strobe the skeleton color. Landmarks are in the un-mirrored
      // camera frame, so the user's physical RIGHT hand has a LOWER
      // wrist x (camera sees it on its left). When both hands are
      // present we compare wrists pairwise; with only one hand, we
      // threshold at 0.5.
      const other = snap === left ? right : left;
      const myX = snap.landmarks[0]!.x;
      const otherX =
        other.present && other.landmarks ? other.landmarks[0]!.x : null;
      const physicalRight = otherX !== null ? myX < otherX : myX < 0.5;
      const displayedId: HandId = physicalRight ? 'right' : 'left';
      const color = COLORS[displayedId];
      const lm = snap.landmarks;
      const toX = (x: number) => x * canvas.width;
      const toY = (y: number) => y * canvas.height;

      // Active bones — thumb/index/middle, full opacity.
      ctx.strokeStyle = color;
      ctx.lineWidth = 3 * devicePixelRatio;
      ctx.beginPath();
      for (const [a, b] of ACTIVE_CONNECTIONS) {
        const pa = lm[a]!;
        const pb = lm[b]!;
        ctx.moveTo(toX(pa.x), toY(pa.y));
        ctx.lineTo(toX(pb.x), toY(pb.y));
      }
      ctx.stroke();

      // Dim bones — ring/pinky, faded so they don't distract.
      ctx.globalAlpha = DIM_ALPHA;
      ctx.beginPath();
      for (const [a, b] of DIM_CONNECTIONS) {
        const pa = lm[a]!;
        const pb = lm[b]!;
        ctx.moveTo(toX(pa.x), toY(pa.y));
        ctx.lineTo(toX(pb.x), toY(pb.y));
      }
      ctx.stroke();
      ctx.globalAlpha = 1;

      // Landmark dots — matching per-landmark opacity.
      ctx.fillStyle = color;
      for (let i = 0; i < lm.length; i++) {
        const p = lm[i]!;
        ctx.globalAlpha = DIM_LANDMARKS.has(i) ? DIM_ALPHA : 1;
        ctx.beginPath();
        ctx.arc(toX(p.x), toY(p.y), 3 * devicePixelRatio, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
  });

  return <canvas ref={ref} className="hand-overlay" />;
}
